/**
 * UBER CRITICAL UPDATE - 8/7/2025
 * Fixed message count initialization bug (line 59)
 * Changed: messageCount: 1 → messageCount: 0
 * Previous: Double-counting messages (initial 1 + increment(1) = 2 for single message)
 * Impact: Fixed +1 message count bug in chat history panel
 * Revert: git checkout d0e0cb9 -- frontend/src/utils/db-messaging.js
 * 
 * UPDATE - 8/7/2025 (totalMessageCount tracking)
 * Added lifetime message counter on user document
 * Changes:
 * - saveMessage (lines 98-101): Increments totalMessageCount on user doc after message saved
 * - deleteMessage (lines 396-398): Decrements totalMessageCount on user doc after message deleted
 * Impact: Tracks total messages across all conversations (never resets)
 * Revert: Remove updateDoc calls with totalMessageCount: increment() in saveMessage and deleteMessage
 */

import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  limit,
  doc,
  getDoc,
  where,
  onSnapshot,
  Timestamp,
  updateDoc,  // Add this
  increment,  // Add this
  serverTimestamp,
  deleteDoc,
  writeBatch,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase';

// Cache to store message references
const messageCache = new Map();

// Helper to convert Firestore timestamps
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  return new Date(timestamp);
};

export const saveMessage = async (userId, message, conversationId = null) => {
  if (!userId) return null;

  const batch = writeBatch(db);
  
  try {
    // If no conversationId, create new conversation
    if (!conversationId) {
      const conversationsRef = collection(db, 'users', userId, 'conversations');
      const newConvoRef = doc(conversationsRef);
      conversationId = newConvoRef.id;
      
      batch.set(newConvoRef, {
        metadata: {
          title: message.text.slice(0, 50) + (message.text.length > 50 ? '...' : ''),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          messageCount: 0  // Start at 0, will be incremented to 1 below
        },
        lastMessage: message.text,
        participants: [userId],
        archived: false
      });
    }

    // Add message to the conversation
    const messagesRef = collection(
      db, 
      'users', 
      userId, 
      'conversations', 
      conversationId, 
      'messages'
    );
    
    const messageRef = doc(messagesRef);
    
    batch.set(messageRef, {
      sender: message.sender,
      content: message.text,
      timestamp: serverTimestamp(),
      type: 'text',
      status: 'sent'
    });

    // Update conversation metadata
    const convoRef = doc(db, 'users', userId, 'conversations', conversationId);
    batch.update(convoRef, {
      'metadata.updatedAt': serverTimestamp(),
      'metadata.messageCount': increment(1),
      lastMessage: message.text
    });

    await batch.commit();

    // Increment total message count on user document
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      totalMessageCount: increment(1)
    });

    return {
      messageId: messageRef.id,
      conversationId
    };
  } catch (error) {
    console.error('Error in saveMessage:', error);
    throw error;
  }
};


// Add new archiveMessages function
export const archiveMessages = async (userId) => {
  if (!userId) return;

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return;

    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const snapshot = await getDocs(conversationsRef);
    
    let batch = writeBatch(db);
    let count = 0;
    
    for (const doc of snapshot.docs) {
      batch.update(doc.ref, {
        archived: true,
        'metadata.archivedAt': serverTimestamp()
      });
      count++;
      
      if (count >= 500) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }

    if (count > 0) {
      await batch.commit();
    }

    messageCache.clear();
  } catch (error) {
    console.error('Error archiving messages:', error);
    throw error;
  }
};

// Keep the old fetchMessages function temporarily during transition
export const fetchMessages = async (userId, limitCount = 10, startAfter = null) => {
if (!userId) {
  console.log('No userId provided to fetchMessages');
  return [];
}

try {
  // Get the most recent conversation first
  const conversationsRef = collection(db, 'users', userId, 'conversations');
  const convQuery = query(
    conversationsRef,
    where('archived', '==', false),  // Add this to filter out archived
    orderBy('metadata.updatedAt', 'desc'),  // Changed from lastUpdated to metadata.updatedAt
    limit(1)
  );
  
  const convSnapshot = await getDocs(convQuery);
  
  if (convSnapshot.empty) {
    return [];
  }

  // Get messages from the most recent conversation
  const conversationId = convSnapshot.docs[0].id;
  const messagesRef = collection(
    db, 
    'users', 
    userId, 
    'conversations', 
    conversationId, 
    'messages'
  );

  let q = query(
    messagesRef,
    orderBy('timestamp', 'asc'),  // Changed to asc for chronological order
    limit(limitCount)
  );

  if (startAfter) {
    q = query(q, startAfter(startAfter));
  }

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    text: doc.data().content, // Map content to text for consistency
    timestamp: convertTimestamp(doc.data().timestamp),
  }));
} catch (error) {
  console.error('Error fetching messages:', error);
  return [];
}
};

// Enhanced fetchMessagesForConversation to get complete history
//  limit size of loaded messages
// const MESSAGES_PER_PAGE = 50; 
export const fetchMessagesForConversation = async (userId, conversationId) => {
if (!userId || !conversationId) return [];

try {
  const messagesRef = collection(
    db, 
    'users', 
    userId, 
    'conversations', 
    conversationId, 
    'messages'
  );

  const q = query(
    messagesRef,
    orderBy('timestamp', 'asc')  // Removed limit to get full history
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    text: doc.data().content,  // Maintain consistency in message format
    timestamp: convertTimestamp(doc.data().timestamp)
  }));
} catch (error) {
  console.error('Error fetching messages:', error);
  return [];
}
};

// Add a new helper function for conversation details
export const fetchConversationDetails = async (userId, conversationId) => {
if (!userId || !conversationId) return null;

try {
  const conversationRef = doc(
    db, 
    'users', 
    userId, 
    'conversations', 
    conversationId
  );
  
  const conversationDoc = await getDoc(conversationRef);
  
  if (!conversationDoc.exists()) return null;

  return {
    id: conversationDoc.id,
    ...conversationDoc.data(),
    metadata: {
      ...conversationDoc.data().metadata,
      updatedAt: convertTimestamp(conversationDoc.data().metadata?.updatedAt),
      createdAt: convertTimestamp(conversationDoc.data().metadata?.createdAt)
    }
  };
} catch (error) {
  console.error('Error fetching conversation details:', error);
  return null;
}
};


export const subscribeToMessages = (userId, conversationId, callback) => {
  if (!userId || !conversationId) {
    console.log('Missing required parameters in subscribeToMessages');
    return null;
  }

  try {
    // Direct subscription to specific conversation's messages
    const messagesRef = collection(
      db, 
      'users', 
      userId, 
      'conversations', 
      conversationId, 
      'messages'
    );

    const q = query(
      messagesRef,
      orderBy('timestamp', 'asc')  // Removed limit to get full history
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          text: doc.data().content,  // Maintain consistency in message format
          timestamp: convertTimestamp(doc.data().timestamp),
        }));
        callback(messages);
      },
      (error) => {
        console.error('Error in message subscription:', error);
        callback([]);
      }
    );

  } catch (error) {
    console.error('Error setting up message subscription:', error);
    return null;
  }
};

export const subscribeToConversations = (userId, callback) => {
  if (!userId) {
    console.log('No userId provided to subscribeToConversations');
    return null;
  }

  try {
    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const q = query(
      conversationsRef,
      where('archived', '==', false),
      orderBy('metadata.updatedAt', 'desc'),
      limit(20)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const conversations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          metadata: {
            ...doc.data().metadata,
            updatedAt: convertTimestamp(doc.data().metadata?.updatedAt),
            createdAt: convertTimestamp(doc.data().metadata?.createdAt)
          },
          lastMessage: doc.data().lastMessage || ''
        }));
        callback(conversations);
      },
      (error) => {
        console.error('Error in conversations subscription:', error);
        callback([]);
      }
    );

  } catch (error) {
    console.error('Error setting up conversations subscription:', error);
    return null;
  }
};

// Delete a message
export const deleteMessage = async (userId, conversationId, messageId) => {
  if (!userId || !conversationId || !messageId) return;

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists() || userDoc.data().tier !== 'pro') return;

    const messageRef = doc(
      db, 
      'users', 
      userId, 
      'conversations', 
      conversationId, 
      'messages', 
      messageId
    );
    await deleteDoc(messageRef);
    messageCache.delete(messageId);

    // Update conversation message count
    const conversationRef = doc(db, 'users', userId, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      'metadata.messageCount': increment(-1)
    });

    // Decrement total message count on user document
    await updateDoc(userRef, {
      totalMessageCount: increment(-1)
    });
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

// Delete all messages for a user
export const clearAllMessages = async (userId) => {
  if (!userId) return;

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists() || userDoc.data().tier !== 'pro') return;

    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const conversationsSnapshot = await getDocs(conversationsRef);
    
    let batch = writeBatch(db);
    let count = 0;

    // First, mark all conversations as archived
    for (const conversationDoc of conversationsSnapshot.docs) {
      const messagesRef = collection(conversationDoc.ref, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);

      // Delete all messages in the conversation
      for (const messageDoc of messagesSnapshot.docs) {
        batch.delete(messageDoc.ref);
        count++;
        
        if (count >= 500) {
          await batch.commit();
          batch = writeBatch(db);
          count = 0;
        }
      }

      // Update conversation metadata
      batch.update(conversationDoc.ref, {
        archived: true,
        'metadata.messageCount': 0,
        'metadata.archivedAt': serverTimestamp()
      });
    }

    if (count > 0) {
      await batch.commit();
    }

    messageCache.clear();
  } catch (error) {
    console.error('Error clearing messages:', error);
    throw error;
  }
};

export const fetchConversations = async (userId, limitCount = 10) => {
  if (!userId) {
    console.log('No userId provided to fetchConversations');
    return [];
  }

  try {
    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const q = query(
      conversationsRef,
      where('archived', '==', false),
      orderBy('metadata.updatedAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      metadata: {
        ...doc.data().metadata,
        updatedAt: convertTimestamp(doc.data().metadata?.updatedAt),
        createdAt: convertTimestamp(doc.data().metadata?.createdAt)
      },
      lastMessage: doc.data().lastMessage || ''
    }));

  } catch (error) {
    console.error('Error fetching conversations:', error);
    return [];
  }
};

// Add this function to db-messaging.js
export const archiveConversation = async (userId, conversationId) => {
if (!userId || !conversationId) return;

try {
  const conversationRef = doc(db, 'users', userId, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    archived: true,
    'metadata.archivedAt': serverTimestamp()
  });
} catch (error) {
  console.error('Error archiving conversation:', error);
  throw error;
}
};

    export const updateExistingConversations = async (userId) => {
      if (!userId) return;
    
      try {
        const conversationsRef = collection(db, 'users', userId, 'conversations');
        const snapshot = await getDocs(conversationsRef);
        
        let batch = writeBatch(db);
        let count = 0;
        
        for (const doc of snapshot.docs) {
          const existingData = doc.data();
          
          // Prepare metadata structure
          const metadata = {
            createdAt: existingData.createdAt || serverTimestamp(),
            updatedAt: existingData.lastUpdated || serverTimestamp(),
            messageCount: existingData.messageCount || 0,
            title: existingData.lastMessage?.slice(0, 50) || 'Previous Conversation',
            lastViewed: null
          };
    
          // Update document with complete structure
          batch.update(doc.ref, {
            metadata,
            archived: false,
            lastMessage: '',  // We'll populate this later if needed
            participants: [userId], // Add current user as participant
            type: 'chat',  // Default type for existing conversations
            // Remove old fields after moving them to metadata
            createdAt: deleteField(),
            lastUpdated: deleteField(),
            messageCount: deleteField()
          });
    
          count++;
          
          if (count >= 500) {
            await batch.commit();
            batch = writeBatch(db);
            count = 0;
          }
        }
    
        if (count > 0) {
          await batch.commit();
        }
    
        console.log('Successfully updated existing conversations with new schema');
        return true;
      } catch (error) {
        console.error('Error updating conversations:', error);
        throw error;
      }
    };

